/**
 * Time formatting, parsing, and option generation helpers.
 * Uses moment-timezone for all conversions to ensure timezone safety.
 */
import moment from 'moment-timezone';

// ---------------------------------------------------------------------------
// Time interval options (15-min increments, 12hr format)
// ---------------------------------------------------------------------------

export interface TimeOption {
  value: string;
  label: string;
}

export function generateTimeOptions(interval = 15): TimeOption[] {
  const options: TimeOption[] = [];
  for (let m = 0; m < 24 * 60; m += interval) {
    const h = Math.floor(m / 60);
    const min = m % 60;
    const period = h < 12 ? 'AM' : 'PM';
    const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const label = `${String(display).padStart(2, '0')}:${String(min).padStart(2, '0')} ${period}`;
    options.push({ value: label, label });
  }
  return options;
}

export const TIME_OPTIONS = generateTimeOptions();

// ---------------------------------------------------------------------------
// Parsing & formatting
// ---------------------------------------------------------------------------

/** Parse "HH:MM AM/PM" → { hours, minutes } in 24hr, or null if invalid. */
export function parse12hrTime(
  timeStr: string
): { hours: number; minutes: number } | null {
  const match = timeStr?.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  let h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'AM' && h === 12) h = 0;
  if (period === 'PM' && h !== 12) h += 12;
  return { hours: h, minutes: m };
}

interface FlexibleTime {
  /** 1–12 */
  hours: number;
  /** 0–59 */
  minutes: number;
  /** Resolved period, or null for an ambiguous bare 12-hour input. */
  period: 'AM' | 'PM' | null;
}

/**
 * Parse a free-typed time into 12h parts. Ports legacy
 * `KInterviewScheduler` `normalizeTimeInput` separator/digit + 24h→12h
 * rules. Returns `period: null` for ambiguous bare 12-hour input
 * (no AM/PM, hour 1–12).
 */
export function parseFlexibleTime(input: string): FlexibleTime | null {
  if (!input || typeof input !== 'string') return null;
  let s = input.trim().toLowerCase();
  if (!s) return null;

  // Period indicator: a single `a`/`p` is enough ("222p" → PM), and
  // any of `am`/`pm`/`a.m.`/`p.m.`/`p`/`a` with stray dots/spaces is
  // accepted. Captured and stripped before the numeric parse; absent →
  // null (caller defaults to AM).
  let period: 'AM' | 'PM' | null = null;
  const periodMatch = s.match(/([ap])\s*\.?\s*m?\s*\.?\s*$/);
  if (periodMatch && periodMatch.index !== undefined) {
    period = periodMatch[1] === 'p' ? 'PM' : 'AM';
    s = s.slice(0, periodMatch.index).trim();
  }

  // Dots/spaces → colon, collapse repeats, trim stray colons.
  s = s
    .replace(/[.\s]+/g, ':')
    .replace(/:+/g, ':')
    .replace(/^:+|:+$/g, '');

  let hours: number;
  let minutes: number;
  if (s.includes(':')) {
    const parts = s.split(':');
    if (parts.length !== 2) return null;
    hours = parseInt(parts[0], 10);
    minutes = parseInt(parts[1], 10);
  } else {
    if (!/^\d+$/.test(s)) return null;
    if (s.length <= 2) {
      hours = parseInt(s, 10);
      minutes = 0;
    } else if (s.length === 3) {
      hours = parseInt(s.slice(0, 1), 10);
      minutes = parseInt(s.slice(1), 10);
    } else if (s.length === 4) {
      hours = parseInt(s.slice(0, 2), 10);
      minutes = parseInt(s.slice(2), 10);
    } else {
      return null;
    }
  }
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

  if (hours > 12 && hours <= 23) {
    period = period ?? 'PM';
    hours -= 12;
  } else if (hours === 0) {
    hours = 12;
    period = period ?? 'AM';
  } else if (hours > 23) {
    return null;
  }

  if (hours < 1 || hours > 12) return null;
  if (minutes < 0 || minutes > 59) return null;
  return { hours, minutes, period };
}

/**
 * Canonicalize a free-typed time to the grid format ("hh:mm A"), e.g.
 * "4:43 pm" / "443pm" / "1643" / "0443" → "04:43 PM". Ambiguous bare
 * 12-hour input defaults to AM (deterministic). Returns null when
 * unparseable so the creatable Select can reject the entry. Lets
 * recruiters pick odd minutes (04:43) the 15-min grid omits — parity
 * with legacy prod. Wired as kula Select `createOptionValue`, so the
 * committed value is always canonical (no raw↔canonical desync).
 */
export function normalizeTo12h(input: string): string | null {
  const p = parseFlexibleTime(input);
  if (!p) return null;
  const period = p.period ?? 'AM';
  return `${String(p.hours).padStart(2, '0')}:${String(p.minutes).padStart(2, '0')} ${period}`;
}

/**
 * react-select `filterOption` for the creatable time selects. kula
 * hardcodes the default substring filter for creatable, which drops the
 * synthetic "Create …" entry (its label is the normalized value, e.g.
 * "03:33 AM", which doesn't substring-match the raw input "333") →
 * "No options". This keeps any option whose value equals the normalized
 * form of what was typed (covers the create entry AND an exact grid
 * match), and otherwise falls back to the normal case-insensitive
 * label/value substring match so grid search still narrows.
 */
export function timeSelectFilterOption(
  option: { label?: string; value?: string },
  rawInput: string
): boolean {
  const input = (rawInput ?? '').trim();
  if (input === '') return true;
  const norm = normalizeTo12h(input);
  if (norm && option.value === norm) return true;
  const hay = `${option.label ?? ''} ${option.value ?? ''}`.toLowerCase();
  return hay.includes(input.toLowerCase());
}

/**
 * `isValidNewOption` for the creatable time selects. Allows a "Create …"
 * entry only when the typed input parses to a time AND that canonical
 * value is NOT already an option (grid or the injected current value).
 * This is what prevents the duplicate "03:33 AM" — when the value
 * already exists the user just picks it; Create is suppressed.
 */
export function isNewTimeOption(
  inputValue: string,
  _value: unknown,
  options: ReadonlyArray<unknown>
): boolean {
  const norm = normalizeTo12h(inputValue);
  if (!norm) return false;
  const flat: Array<{ value?: string }> = [];
  for (const o of options) {
    if (o && typeof o === 'object' && 'options' in o) {
      flat.push(
        ...((o as { options?: Array<{ value?: string }> }).options ?? [])
      );
    } else if (o && typeof o === 'object') {
      flat.push(o as { value?: string });
    }
  }
  return !flat.some((o) => o.value === norm);
}

/** Format an ISO string to "02:30 PM" using timezone. */
export function formatTime12(iso: string, timezone?: string): string {
  if (!iso) return '';
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(iso, tz);
  if (!m.isValid()) return '';
  return m.format('hh:mm A');
}

/** Format an ISO string to a readable date using timezone. */
export function formatDateDisplay(iso: string, timezone?: string): string {
  if (!iso) return '';
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(iso, tz);
  if (!m.isValid()) return '';
  return m.format('MM/DD/YYYY');
}

// ---------------------------------------------------------------------------
// Building ISO strings
// ---------------------------------------------------------------------------

/**
 * A JS `Date` positioned so its BROWSER-LOCAL calendar day equals the wall-clock
 * day of `iso` in `timezone`.
 *
 * The date pickers store plain `Date`s and every consumer reads them back with
 * browser-local getters (`buildIsoFromDateAndTime` does `moment(date).format('YYYY-MM-DD')`).
 * Seeding a picker with `new Date(iso)` stores an absolute instant whose
 * browser-local day is the BROWSER's day — which differs from the interview's
 * day whenever the browser zone and the selected zone straddle midnight for that
 * instant. Recombining that browser-local day with a time formatted in the
 * selected zone then shifts the slot by a whole day (ENG-3526). Anchoring the
 * seed to the selected-zone wall day keeps both halves in one frame, so the
 * round-trip is stable regardless of the recruiter's browser timezone.
 */
export function wallDateInTz(iso: string, timezone?: string): Date {
  if (!iso) return new Date(NaN);
  const tz = timezone || moment.tz.guess();
  const m = moment.tz(iso, tz);
  if (!m.isValid()) return new Date(NaN);
  // Local midnight on the selected-zone calendar day. Date pickers are
  // date-only; the time is carried separately by the start/end time fields.
  return new Date(m.year(), m.month(), m.date());
}

/** Build an ISO string from a Date (for the date part) + a 12hr time string + timezone. */
export function buildIsoFromDateAndTime(
  date: Date | null,
  timeStr: string,
  timezone?: string
): string {
  if (!date || !timeStr) return '';
  const parsed = parse12hrTime(timeStr);
  if (!parsed) return '';
  const tz = timezone || moment.tz.guess();
  const dateStr = moment(date).format('YYYY-MM-DD');
  const timeFormatted = `${String(parsed.hours).padStart(2, '0')}:${String(parsed.minutes).padStart(2, '0')}`;
  const m = moment.tz(`${dateStr} ${timeFormatted}`, 'YYYY-MM-DD HH:mm', tz);
  return m.isValid() ? m.toISOString() : '';
}

/** Add `minutes` to an ISO string. Returns new ISO or ''. */
export function addMinutesToIso(iso: string, minutes: number): string {
  if (!iso) return '';
  const m = moment(iso);
  if (!m.isValid()) return '';
  return m.add(minutes, 'minutes').toISOString();
}

/** Diff in minutes between two ISO strings. */
export function diffMinutesIso(startIso: string, endIso: string): number {
  const s = moment(startIso);
  const e = moment(endIso);
  if (!s.isValid() || !e.isValid()) return 0;
  return Math.round(e.diff(s, 'minutes'));
}

/** Snap a duration to the nearest interval (default 15 min). */
export function snapToNearest(minutes: number, interval = 15): number {
  if (minutes <= 0) return interval;
  return Math.round(minutes / interval) * interval || interval;
}

/** Find the closest time option value for a given 12hr time string. */
export function findClosestTimeOption(
  timeStr: string,
  options: TimeOption[] = TIME_OPTIONS
): string {
  const parsed = parse12hrTime(timeStr);
  if (!parsed) return options[0]?.value ?? '';
  const targetMinutes = parsed.hours * 60 + parsed.minutes;

  let closest = options[0]?.value ?? '';
  let minDiff = Infinity;

  for (const opt of options) {
    const optParsed = parse12hrTime(opt.value);
    if (!optParsed) continue;
    const optMinutes = optParsed.hours * 60 + optParsed.minutes;
    const diff = Math.abs(optMinutes - targetMinutes);
    if (diff < minDiff) {
      minDiff = diff;
      closest = opt.value;
    }
  }
  return closest;
}

/**
 * Find the closest time option **at or after** a given 12hr time string —
 * i.e. ceiling to the next 15-min slot. Used for the End Time field so
 * a sub-15-min duration (e.g. start=10:00 + duration=6) does not snap
 * back to 10:00 (== startTime), which the End Time `<Select>` would then
 * auto-correct away — destroying the sub-15 duration the user picked.
 */
export function findCeilingTimeOption(
  timeStr: string,
  options: TimeOption[] = TIME_OPTIONS
): string {
  const parsed = parse12hrTime(timeStr);
  if (!parsed) return options[0]?.value ?? '';
  const targetMinutes = parsed.hours * 60 + parsed.minutes;

  let ceilingValue = '';
  let ceilingMinutes = Infinity;

  for (const opt of options) {
    const optParsed = parse12hrTime(opt.value);
    if (!optParsed) continue;
    const optMinutes = optParsed.hours * 60 + optParsed.minutes;
    if (optMinutes >= targetMinutes && optMinutes < ceilingMinutes) {
      ceilingMinutes = optMinutes;
      ceilingValue = opt.value;
    }
  }

  // No option at or after target — fall back to the latest option.
  return ceilingValue || options[options.length - 1]?.value || '';
}
